import clsx from 'clsx';

import type {
  DiscountType,
  ProductTableBox,
} from '@flow/libs/data-validators/entity-schemas/document-box/product-table';
import useCurrentPriceRoundingMethod from '@app/hooks/use-current-price-rounding-method';
import useFeatureFlag from '@app/hooks/use-feature-flag';
import { formatNumberWithTrailingZeros } from '@flow/libs/utils/format-number-with-trailing-zeros';
import { roundBy } from '@flow/libs/utils/math';

import {
  PRODUCT_DISCOUNT_TYPE_PERCENTAGE,
  PRODUCT_TAX_TYPE_PERCENTAGE,
} from '@flow/libs/agreement/boxes/product-constants';
import {
  hasZeroPriceWithPercentageDiscount,
  isBasePriceVisible,
} from '@app/components/contract-boxes/product-table-box/product-table-box-helpers';
import { getTaxAmountAsPercentage } from '@app/components/contract-boxes/product-table-box/product-table/popup-forms/update-product-helpers';
import AffixedPrice from '@app/components/contract-boxes/product-table-box/product-table/affixed-price';
import { T } from '@app/components/translation';

import style from './expanded-product-price.module.scss';

type Props = {
  price: number;
  discountAmount: number;
  priceDiscounted: number;
  discountType: DiscountType | undefined;
  prefix: ProductTableBox['config']['affixes']['prefix'];
  postfix: ProductTableBox['config']['affixes']['postfix'];
  pricePrecision: ProductTableBox['config']['pricePrecision'];
  taxAmount?: number;
  taxDisplay?: string;
  taxType?: number;
  effectiveTaxAmount?: number;
};

const ExpandedProductPrice = ({
  price,
  discountAmount,
  priceDiscounted,
  discountType,
  prefix,
  postfix,
  pricePrecision,
  taxAmount,
  taxDisplay,
  taxType,
  effectiveTaxAmount,
}: Props) => {
  const isProductTaxEnabled = useFeatureFlag('temporaryProductTax');
  const currentPriceRoundingMethod = useCurrentPriceRoundingMethod();
  const hasDiscount = Boolean(discountAmount);
  const hasTaxAmount = Boolean(taxAmount);
  const hasMultipliedByZero = hasZeroPriceWithPercentageDiscount(
    discountAmount,
    price,
    discountType as number | undefined,
  );

  const shouldShowBasePrice = isBasePriceVisible(price, priceDiscounted);

  const renderDiscountedPrice = () => {
    if (!hasDiscount) {
      return null;
    }
    return (
      <div
        data-testid="discounted-price"
        className={style.DiscountedPrice}
      >
        <AffixedPrice
          formattedPrice={formatNumberWithTrailingZeros(
            roundBy(currentPriceRoundingMethod, priceDiscounted, pricePrecision),
            pricePrecision,
          )}
          prefix={prefix}
          postfix={postfix}
        />
      </div>
    );
  };

  const renderDiscountAmount = () => {
    if (!hasDiscount || typeof discountAmount !== 'number' || priceDiscounted < 0) {
      return null;
    }

    const roundedDiscountAmount = formatNumberWithTrailingZeros(
      roundBy(currentPriceRoundingMethod, discountAmount * 100, pricePrecision),
      pricePrecision,
    );

    if (discountType === PRODUCT_DISCOUNT_TYPE_PERCENTAGE) {
      const percentageDiscount = (
        <span>{roundBy(currentPriceRoundingMethod, discountAmount * 100, pricePrecision)} %</span>
      );

      return (
        <span data-testid="discount-amount-percentage">
          <T
            _str="{discountAmount} discount"
            _comment="The amount discounted from the product price"
            discountAmount={
              discountType === PRODUCT_DISCOUNT_TYPE_PERCENTAGE
                ? percentageDiscount
                : roundedDiscountAmount
            }
            noWrap
          />
        </span>
      );
    }
    const affixedDiscount = (
      <span>
        <AffixedPrice
          formattedPrice={formatNumberWithTrailingZeros(
            roundBy(currentPriceRoundingMethod, discountAmount, pricePrecision),
            pricePrecision,
          )}
          prefix={prefix}
          postfix={postfix}
        />
      </span>
    );
    return (
      <span data-testid="discount-amount-numeric">
        <T
          _str="{discountAmount} discount"
          _comment="The amount discounted from the product price"
          discountAmount={affixedDiscount}
          noWrap
        />
      </span>
    );
  };

  const renderTaxAmount = () => {
    if (!hasTaxAmount || typeof taxAmount !== 'number' || taxAmount < 0) {
      return null;
    }

    const formattedTaxAmount = formatNumberWithTrailingZeros(
      roundBy(currentPriceRoundingMethod, taxAmount, pricePrecision),
      pricePrecision,
    );

    const formattedEffectiveTaxAmount = formatNumberWithTrailingZeros(
      roundBy(currentPriceRoundingMethod, effectiveTaxAmount, pricePrecision),
      pricePrecision,
    );

    const percentageTax = getTaxAmountAsPercentage(
      taxAmount,
      currentPriceRoundingMethod,
      pricePrecision,
    );

    return (
      <div className={style.TaxAmount}>
        {taxDisplay}
        {` (${percentageTax}): ${prefix}${taxType === PRODUCT_TAX_TYPE_PERCENTAGE ? formattedEffectiveTaxAmount : formattedTaxAmount}${postfix}`}
      </div>
    );
  };

  if (hasMultipliedByZero) {
    return null;
  }

  return (
    <div className={style.ProductPrice}>
      <div>
        {shouldShowBasePrice && (
          <div
            data-testid="base-price"
            className={clsx(style.BasePrice, {
              [style.HasDiscount]: hasDiscount,
            })}
          >
            <AffixedPrice
              formattedPrice={formatNumberWithTrailingZeros(
                roundBy(currentPriceRoundingMethod, price, pricePrecision),
                pricePrecision,
              )}
              prefix={prefix}
              postfix={postfix}
            />
          </div>
        )}
        {renderDiscountedPrice()}
      </div>
      <div className={style.DiscountAmount}>{renderDiscountAmount()}</div>
      {isProductTaxEnabled && renderTaxAmount()}
    </div>
  );
};

export default ExpandedProductPrice;
